import { Heart, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/shopify";

/**
 * Boutique product card with Add-to-Cart that opens the in-site cart drawer.
 * Final payment step happens on Shopify's secure hosted checkout.
 */
export default function ProductCard({ product, testIdPrefix = "product" }) {
    const { addItem } = useCart();

    const onAdd = (e) => {
        e.preventDefault();
        if (!product.variantId) {
            window.open(product.productUrl, "_blank", "noopener");
            return;
        }
        addItem(product, 1);
    };

    return (
        <article
            className="category-card flex flex-col"
            data-testid={`${testIdPrefix}-${product.handle}`}
        >
            <a
                href={product.productUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block arch-img relative"
                aria-label={`View ${product.title} on Shopify`}
            >
                {product.image ? (
                    <img
                        src={product.image.url}
                        alt={product.image.altText || product.title}
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full bg-bb-blush-2 flex items-center justify-center text-bb-text-soft">
                        <ShoppingBag size={28} />
                    </div>
                )}
                {product.compareAtPrice && product.price && product.compareAtPrice.amount > product.price.amount && (
                    <span className="absolute top-3 left-3 bg-bb-pink-script text-white text-[0.6rem] tracking-[0.2em] uppercase px-2 py-1 rounded-full">
                        Sale
                    </span>
                )}
                {!product.available && (
                    <span className="absolute top-3 right-3 bg-bb-black text-white text-[0.6rem] tracking-[0.2em] uppercase px-2 py-1 rounded-full">
                        Sold Out
                    </span>
                )}
            </a>
            <div className="px-3 py-5 text-center flex-1 flex flex-col">
                <h3 className="font-serif-display tracking-[0.12em] text-sm md:text-base text-bb-text line-clamp-2">
                    {product.title}
                </h3>
                <div className="mt-2 flex items-center justify-center gap-2">
                    {product.compareAtPrice && (
                        <span className="text-xs text-bb-text-soft line-through">
                            {formatPrice(product.compareAtPrice)}
                        </span>
                    )}
                    {product.price && (
                        <span className="font-serif-display text-bb-pink-script text-base">
                            {formatPrice(product.price)}
                        </span>
                    )}
                </div>
                <div className="mt-auto pt-4 flex justify-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={onAdd}
                        disabled={!product.available}
                        className="btn-blush disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`${testIdPrefix}-add-${product.handle}`}
                    >
                        {product.available ? "Add to Bag" : "Sold Out"}
                        {product.available && <Heart size={12} className="fill-current" />}
                    </button>
                </div>
            </div>
        </article>
    );
}
